import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AppContext';
import TurnstileWidget from '../components/TurnstileWidget';
import styles from './LoginPage.module.css';

/**
 * Página de login. Delega la autenticación al AuthContext, que llama
 * al backend de Ecolab. Al recibir el JWT lo guarda en localStorage
 * y actualiza el estado del contexto.
 *
 * Turnstile: modo Managed según INSTRUCTIVO_TURNSTILE_APPS_FINAL.md v2.0.
 * El botón de login queda deshabilitado hasta que Turnstile emita el token.
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [formData, setFormData]         = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [tsToken, setTsToken]           = useState('');

  // Destino tras login: prioridad al param ?retorno= (viene del interceptor
  // 401 de api.js), luego location.state?.from (React Router), luego /admin.
  const retornoQS = new URLSearchParams(location.search).get('retorno');
  const destino = retornoQS || location.state?.from?.pathname || '/admin';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Bloquear envío si Turnstile no ha emitido token todavía
    if (!tsToken) {
      setError('Espere a que se complete la verificación de seguridad.');
      return;
    }

    setLoading(true);
    try {
      const result = await login(formData.email, formData.password, tsToken);
      if (result.success) {
        navigate(destino, { replace: true });
      } else {
        setError(result.error || 'Credenciales inválidas');
        // Resetear token para el siguiente intento — el widget lo regenera automáticamente
        setTsToken('');
      }
    } catch (err) {
      setError(err.message || 'Error inesperado al iniciar sesión');
      setTsToken('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className={styles.loginPage}>
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1>Iniciar Sesión</h1>
            <p>Accede con tus credenciales del ecosistema Ecolab</p>
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.field}>
              <label>Correo electrónico</label>
              <div className={styles.inputWrapper}>
                <Mail size={18} />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="tu@email.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className={styles.field}>
              <label>Contraseña</label>
              <div className={styles.inputWrapper}>
                <Lock size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className={styles.togglePassword}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Widget Cloudflare Turnstile — modo Managed, visible (~300×65 px) */}
            <TurnstileWidget
              action="login"
              onToken={setTsToken}
              onExpire={() => setTsToken('')}
              onError={(code) => console.warn('Turnstile error:', code)}
            />

            {/* Deshabilitado hasta que Turnstile emita el token */}
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={loading || !tsToken}
            >
              {loading ? 'Ingresando...' : 'Iniciar Sesión'}
            </button>
          </form>

          <div className={styles.footer}>
            <p>
              ¿Aún no tienes credenciales? Contacta a tu cooperativa para
              ser registrado en el ecosistema.
            </p>
            <p><Link to="/">← Volver al inicio</Link></p>
          </div>
        </div>
      </div>
    </main>
  );
}

